import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Link, useNavigate } from 'react-router-dom';
import { Modal, Popover } from 'antd';
import { toast } from 'react-toastify';
import Breadcrumb from '../../components/Breadcrumb';
import { api } from '../../utils/apiClient';
import { useSelector } from 'react-redux';
import Pagination from '../../utils/Pagination';
import LoadingShimmer from '../../utils/LoadingShimmer';
import ContentLoader from '../../utils/ContentLoader';
import SearchBy from '../../utils/SearchBy';
import { TableSortHeaders } from '../../utils/TableSortHeaders';
const { confirm } = Modal;

let searchTimeout;

const RolePermissionTable = ({ entriesPerPage = 10 }) => {
    const viewPermissions = useSelector((state) => state.permission.rolePermissions) || [];
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [delLoading, setDelLoading] = useState(false);
    //pagination
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalEntries, setTotalEntries] = useState(0);
    //sort
    const [sort, setSort] = useState("created_at");
    const [order, setOrder] = useState("desc");
    //search
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 1000);
        return () => clearTimeout(searchTimeout);
    }, [search]);

    const headerConfig = [
        { key: "id", label: "Role ID", sortable: false },
        { key: "name", label: "Role Name", sortable: false },
        { key: "action", label: "Action", sortable: false },
    ];

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                setLoading(true);
                const res = await api.get("/roles", {
                    params: {
                        page,
                        limit,
                        filter: "basic",
                        sort,
                        order,
                        search: debouncedSearch?.trim() || "",
                    }
                });
                setRoles(res.data?.data || []);
                setTotalPages(res.data.pagination.totalPages || 1);
                setTotalEntries(res.data.pagination.total || 0);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchRoles();
    }, [page, limit, sort, order, debouncedSearch]);

    const handleDelete = async (id) => {
        try {
            setDelLoading(true);
            await api.delete(`/roles/${id}`);
            toast.success("Role deleted successfully!");
            setRoles(prev => prev.filter(role => role.id !== id));
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Failed to delete role");
        } finally {
            setDelLoading(false);
        }
    };

    const showDeleteConfirm = (id) => {
        confirm({
            title: 'Are you sure you want to delete this role?',
            okText: 'Yes',
            okType: 'primary',
            cancelText: 'No',
            centered: true,
            okButtonProps: {
                className: "custom-modal-btn custom-delete-btn",
            },
            cancelButtonProps: {
                className: "custom-modal-btn custom-cancel-btn",
            },
            onOk() { handleDelete(id); },
        });
    };

    if (delLoading) return <ContentLoader />;

    return (
        <>
            <Breadcrumb title="Role Access Management" />
            {loading ? (
                <LoadingShimmer
                    columns={3}
                    rows={6}
                    avatarColumnIndex={null}
                    actionColumnIndex={2}
                />
            ) : (
                <div className="card basic-data-table" style={{ marginTop: "11px" }}>
                    <div className="card-header d-flex justify-content-end align-items-center global-header">
                        <SearchBy
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                        />
                        {viewPermissions.includes("role.create") && (
                            <button
                                className="btn btn-primary-600 global-add-btn"
                                onClick={() => navigate("/create-roles")}
                            >
                                <Icon
                                    icon="fa6-regular:square-plus"
                                    className="icon text-lg line-height-1"
                                />
                                Add
                            </button>
                        )}
                    </div>

                    <div className="card-body card-body-modify">
                        <table className="table bordered-table mb-0" id="dataTable" data-page-length={10}>
                            <thead>
                                <tr>
                                    {TableSortHeaders(headerConfig, sort, order, setSort, setOrder, setPage)}
                                </tr>
                            </thead>
                            <tbody>
                                {roles.length > 0 ? (
                                    roles.map(role => (
                                        <tr key={role.id}>
                                            <td>{role.id}</td>
                                            <td>{role.name}</td>
                                            {/* <td className="d-flex align-items-center">
                                                <Link
                                                    to={`/roles/edit/${role.id}`}
                                                    className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                >
                                                    <Icon icon="lucide:edit" />
                                                </Link>
                                                <button
                                                    onClick={() => showDeleteConfirm(role.id)}
                                                    disabled={role.id === "1"}
                                                    className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0 p-0"
                                                    style={{
                                                        lineHeight: 1,
                                                        background: 'inherit',
                                                        font: 'inherit',
                                                        cursor: role.id === "1" ? 'not-allowed' : 'pointer',
                                                        opacity: role.id === "1" ? 0.5 : 1,
                                                    }}
                                                >
                                                    <Icon icon="mingcute:delete-2-line" />
                                                </button>
                                            </td> */}


                                            <td >
                                                <Popover
                                                    trigger="click"
                                                    placement="bottomRight"
                                                    overlayClassName="action-popover"
                                                    content={
                                                        <div className="action-list">
                                                            {/* EDIT */}
                                                            {viewPermissions.includes("role.edit") && (
                                                                <Link
                                                                    to={`/roles/edit/${role.id}`}
                                                                    className="action-item text-success-main"
                                                                >
                                                                    <Icon icon="lucide:edit" />
                                                                    <span>Edit</span>
                                                                </Link>
                                                            )}
                                                            {/* DELETE */}
                                                            {viewPermissions.includes("role.delete") && (
                                                                <button
                                                                    className="action-item text-danger-main"
                                                                    onClick={() => showDeleteConfirm(role.id)}
                                                                >
                                                                    <Icon icon="mingcute:delete-2-line" />
                                                                    <span>Delete</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    }
                                                >
                                                    <button
                                                        type="button"
                                                        className="w-32-px h-32-px bg-neutral-300 rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                                                    >
                                                        <Icon icon="mdi:dots-vertical" />
                                                    </button>
                                                </Popover>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={3} className="text-center">
                                            No roles found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <Pagination
                            page={page}
                            setPage={setPage}
                            totalPages={totalPages}
                            totalEntries={totalEntries}
                            entriesPerPage={entriesPerPage}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default RolePermissionTable;